import axios from 'axios';
import {isArray} from '../../main/utils';
import store from '../../main/vue-store';
import interceptors from '../interceptors';

interceptors();

let sapBaseUrl = '';
const documentMetaApiUrlElement = document.head.querySelector('meta[name="sap-commerce-api-url"]');
if (documentMetaApiUrlElement) {
  sapBaseUrl = documentMetaApiUrlElement.content;
}

const drupalBaseUrl = `${window.origin}/marketplace/api`;

export const marketplaceApi = {
  async getCurrentCart({sync = false, references = false} = {}) {
    try {
      const cartId = await this.getOrCreateCart();
      const userId = store.getters['marketplace/userId'];
      const cartRes = await axios.get(`${sapBaseUrl}/users/${userId}/carts/${cartId}?sync=${sync}&fields=FULL`);
      let referencesRes;

      if (references) {
        referencesRes = await axios.get(
          `${sapBaseUrl}/users/${userId}/carts/${cartId}/references?referenceType=SIMILAR&fields=FULL&lang=en&curr=USD`
        );
      }

      return {
        cartId: cartId,
        cartResponse: cartRes.data,
        cartReferences: references ? referencesRes.data.references : []
      };
    } catch (e) {
      return {
        cartId: await this.getOrCreateCartFromClearedState(),
        cartResponse: null,
        cartReferences: []
      };
    }
  },
  async productSearch(skus) {
    if (!isArray(skus)) skus = [skus];

    return await axios.get(
      `${sapBaseUrl}/products/search?query=${skus.join(' ')}&currentPage=0&pageSize=1&fields=DEFAULT&lang=en&curr=USD`,
      {excludeAccessToken: true}
    );
  },
  async getProductsReferences(skus) {
    if (!isArray(skus)) skus = [skus];

    return await axios.get(
      `${sapBaseUrl}/products/references?refProducts=${skus.join(' ')}&referenceType=SIMILAR&currentPage=0&pageSize=20&fields=FULL&lang=en&curr=USD`,
      {excludeAccessToken: true}
    );
  },
  async getOrCreateCart() {
    if (!store.getters['auth/authenticated'] && store.getters['marketplace/cartId']) {
      return store.getters['marketplace/cartId'];
    }
    if (!store.getters['auth/authenticated'] && !store.getters['marketplace/cartId']) {
      const cartRes = await axios.post(`${sapBaseUrl}/users/anonymous/carts?fields=DEFAULT`);
      return cartRes.data.guid;
    }

    if (store.getters['auth/authenticated']) {
      return await this.getCurrentUserCartId();
    }

    return '';
  },
  async getOrCreateCartFromClearedState() {
    store.commit('marketplace/reset_cart_data');

    return await this.getOrCreateCart();
  },
  async getCurrentUserCartId() {
    const userRes = await axios.get(`${sapBaseUrl}/users/current?fields=DEFAULT`);
    if (userRes.data.sessionCartId) {
      return userRes.data.sessionCartId;
    }

    const cartRes = await axios.post(`${sapBaseUrl}/users/current/carts?fields=DEFAULT`);
    return cartRes.data.code;
  },
  async mergeCartWithExistingUserCart(oldCartId, mergeCartGuid) {
    await axios.post(
      `${sapBaseUrl}/users/current/carts?fields=DEFAULT&oldCartId=${oldCartId}&toMergeCartGuid=${mergeCartGuid}`
    );
  },
  async getCartId() {
    if (!store.getters['marketplace/cartId']) {
      await store.dispatch('marketplace/getCurrentCart');
      return store.getters['marketplace/cartId'];
    }

    return store.getters['marketplace/cartId'];
  },
  async addToCart(sku, quantity = 1, frequency = null) {
    const userId = store.getters['marketplace/userId'];
    const cartId = await this.getCartId();

    return await axios.post(
      `${sapBaseUrl}/users/${userId}/carts/${cartId}/entries?fields=DEFAULT`,
      {quantity, frequency, product: {code: sku}}
    );
  },
  async updateCartEntry(sku, quantity, frequency = null, entryNumber) {
    const userId = store.getters['marketplace/userId'];
    const cartId = await this.getCartId();

    return await axios.patch(
      `${sapBaseUrl}/users/${userId}/carts/${cartId}/entries/${entryNumber}?fields=DEFAULT`,
      {quantity, frequency, product: {code: sku}}
    );
  },
  async removeCartEntry(entryNumber) {
    const userId = store.getters['marketplace/userId'];
    const cartId = await this.getCartId();

    await axios.delete(
      `${sapBaseUrl}/users/${userId}/carts/${cartId}/entries/${entryNumber}?fields=DEFAULT`
    );
  },
  async updateCartEntryQuantity(entryNumber, quantity, frequency) {
    const userId = store.getters['marketplace/userId'];
    const cartId = await this.getCartId();

    await axios.patch(
      `${sapBaseUrl}/users/${userId}/carts/${cartId}/entries/${entryNumber}?fields=DEFAULT`,
      {quantity, frequency}
    );
  },
  async getSavedCarts() {
    return await axios.get(`${sapBaseUrl}/users/current/carts?savedCartsOnly=true&fields=FULL`);
  },
  async saveCurrentCart(name) {
    const cartId = await this.getCartId();

    return await axios.patch(
      `${sapBaseUrl}/users/current/carts/${cartId}/save?saveCartName=${encodeURIComponent(name)}&fields=DEFAULT`
    );
  },
  async makeCartActive(cartId) {
    return await axios.patch(
      `${sapBaseUrl}/users/current/carts/${cartId}/restoresavedcart`,
      {cartId}
    );
  },
  async deleteCart(cartId) {
    const userId = store.getters['marketplace/userId'];

    return await axios.delete(`${sapBaseUrl}/users/${userId}/carts/${cartId}`);
  },
  async deleteCurrentCart() {
    if (store.getters['marketplace/cartId']) {
      await this.deleteCart(store.getters['marketplace/cartId']);
    }
  },
  async getCountryCodes(type) {
    return await axios.get(`${sapBaseUrl}/countries?type=${type}&lang=en&curr=USD`);
  },
  async getCountryRegions(countryCode) {
    return await axios.get(`${sapBaseUrl}/countries/${countryCode}/regions?fields=FULL`);
  },
  async attachGuestEmailToCart(email) {
    const cartId = await this.getCartId();

    return await axios.put(
      `${drupalBaseUrl}/carts/${cartId}/attach-email`,
      {email}
    );
  },
  async validateAddressWithStrikeIron(address) {
    const userId = store.getters['marketplace/userId'];

    return await axios.post(
      `${sapBaseUrl}/users/${userId}/addresses/verification?fields=FULL`,
      {...address}
    );
  },
  async addCartShippingAddress(data) {
    const userId = store.getters['marketplace/userId'];
    const cartId = await this.getCartId();

    return await axios.post(
      `${sapBaseUrl}/users/${userId}/carts/${cartId}/addresses/delivery?refreshCart=true`,
      data
    );
  },
  async getCurrentCartAddresses() {
    const userId = store.getters['marketplace/userId'];
    const cartId = await this.getCartId();

    return await axios.get(`${sapBaseUrl}/users/${userId}/carts/${cartId}/addresses/delivery?lang=en&curr=USD`);
  },
  async getCurrentCartAddress(addressId) {
    const userId = store.getters['marketplace/userId'];
    const cartId = await this.getCartId();

    return await axios.get(`${sapBaseUrl}/users/${userId}/carts/${cartId}/addresses/delivery/${addressId}?lang=en&curr=USD`);
  },
  async getCurrentUserAddresses() {
    return await axios.get(`${sapBaseUrl}/users/current/addresses`);
  },
  async getCurrentUserAddress(addressId) {
    return await axios.get(`${sapBaseUrl}/users/current/addresses/${addressId}`);
  },
  async addCurrentUserAddress(address) {
    return await axios.post(
      `${sapBaseUrl}/users/current/addresses`,
      {...address}
    );
  },
  async updateCurrentUserAddress(address) {
    return await axios.patch(
      `${sapBaseUrl}/users/current/addresses/${address.id}`,
      {...address}
    );
  },
  async updateCurrentUserCartAddress(address) {
    const cartId = await this.getCartId();

    return await axios.patch(
      `${sapBaseUrl}/users/current/carts/${cartId}/addresses/delivery/${address.id}?lang=en&curr=USD&refreshCart=true`,
      {...address}
    );
  },
  async removeCurrentUserAddress(addressId) {
    return await axios.delete(`${sapBaseUrl}/users/current/addresses/${addressId}`);
  },
  async selectCartShippingAddress(addressId) {
    const userId = store.getters['marketplace/userId'];
    const cartId = await this.getCartId();

    return await axios.put(
      `${sapBaseUrl}/users/${userId}/carts/${cartId}/addresses/delivery?addressId=${addressId}`
    );
  },
  async getCartDeliveryModes(isConsolidate) {
    const userId = store.getters['marketplace/userId'];
    const cartId = await this.getCartId();

    return await axios.post(
      `${drupalBaseUrl}/users/${userId}/carts/${cartId}/deliverymodes`,
      {isConsolidate}
    );
  },
  async selectCartDeliveryModes(deliveryModes) {
    const userId = store.getters['marketplace/userId'];
    const cartId = await this.getCartId();

    return await axios.put(
      `${sapBaseUrl}/users/${userId}/carts/${cartId}/deliverymode?lang=en&curr=USD`,
      {deliveryModes}
    );
  },
  async getPaymetricAccessInfo() {
    const userId = store.getters['marketplace/userId'];
    const hostUri = window.location.origin;

    return await axios.get(
      `${drupalBaseUrl}/users/${userId}/paymetrics/getAccessToken?store=sloan-marketplace&hostUri=${hostUri}`
    );
  },
  async getCurrentCartPaymentDetails() {
    const userId = store.getters['marketplace/userId'];
    const cartId = await this.getCartId();

    return await axios.get(`${sapBaseUrl}/users/${userId}/carts/${cartId}/paymentdetails`);
  },
  async getCurrentUserPaymentDetails() {
    const userId = store.getters['marketplace/userId'];

    return await axios.get(`${sapBaseUrl}/users/${userId}/paymentdetails`);
  },
  async addCartPaymentDetail(accessToken, billingAddress, isDisposable) {
    const userId = store.getters['marketplace/userId'];
    const cartId = await this.getCartId();

    return await axios.post(
      `${sapBaseUrl}/users/${userId}/carts/${cartId}/payment/sop/response?accessToken=${accessToken}&isDisposable=${isDisposable}&lang=en&curr=USD`,
      {...billingAddress}
    );
  },
  async selectCartPaymentDetail(paymentDetailId) {
    const userId = store.getters['marketplace/userId'];
    const cartId = await this.getCartId();

    return await axios.put(
      `${sapBaseUrl}/users/${userId}/carts/${cartId}/paymentdetails?paymentDetailsId=${paymentDetailId}&lang=en&curr=USD`
    );
  },
  async getCurrentUserPaymentDetail(paymentDetailId) {
    return await axios.get(`${sapBaseUrl}/users/current/paymentdetails/${paymentDetailId}`);
  },
  async addCurrentUserPaymentDetail(accessToken, billingAddress, isDefault) {
    const userId = store.getters['marketplace/userId'];

    return await axios.post(
      `${sapBaseUrl}/users/${userId}/paymentdetails/payment/sop/response?accessToken=${accessToken}&isDefault=${isDefault}&lang=en&curr=USD`,
      {...billingAddress}
    );
  },
  async updateCurrentUserPaymentDetail(paymentDetail) {
    return await axios.put(
      `${sapBaseUrl}/users/current/paymentdetails/${paymentDetail.id}` +
      `?expMonth=${paymentDetail.expiryMonth}` +
      `&expYear=${paymentDetail.expiryYear}` +
      `&isDefault=${paymentDetail.defaultPayment}`
    );
  },
  async removeCurrentUserPaymentDetail(paymentDetailId) {
    return await axios.delete(`${sapBaseUrl}/users/current/paymentdetails/${paymentDetailId}`);
  },
  async placeOrder(poNumber) {
    const userId = store.getters['marketplace/userId'];
    const cartId = await this.getCartId();

    return await axios.post(
      `${sapBaseUrl}/users/${userId}/carts/${cartId}/placeOrder?poNumber=${poNumber}&termsChecked=true&fields=FULL&lang=en&curr=USD`
    );
  },
  async updateUserName(data) {
    return await axios.patch(`${sapBaseUrl}/users/current`, {...data});
  },
  async updateUserEmail(email, password) {
    return await axios.put(
      `${sapBaseUrl}/users/current/login?newLogin=${email}&password=${password}`
    );
  },
  async updateUserPassword(oldPassword, newPassword) {
    return await axios.put(
      `${sapBaseUrl}/users/current/password?old=${oldPassword}&new=${newPassword}`
    );
  },
  async getCurrentUserOrders(params) {
    const url = new URL(`${sapBaseUrl}/users/current/orders`);
    url.searchParams.append('fields', 'DEFAULT');
    url.searchParams.append('currentPage', params?.currentPage);
    url.searchParams.append('pageSize', params?.pageSize);
    if (params?.status) url.searchParams.append('status', params?.status);
    if (params?.startDate) url.searchParams.append('startDate', params?.startDate);
    if (params?.endDate) url.searchParams.append('endDate', params?.endDate);
    if (params?.searchTerm) url.searchParams.append('searchTerm', params?.searchTerm);

    return await axios.get(url.toString());
  },
  async getOrder(params) {
    const userId = store.getters['marketplace/userId'];
    const url = new URL(`${drupalBaseUrl}/users/${userId}/orders/orderDetails`);
    if (params?.code) url.searchParams.append('code', params?.code);
    if (params?.email) url.searchParams.append('email', params?.email);
    if (params?.billZip) url.searchParams.append('billZip', params?.billZip);

    return await axios.get(url.toString());
  },
  async getCancelStatuses() {
    const userId = store.getters['marketplace/userId'];

    return await axios.get(`${drupalBaseUrl}/users/${userId}/orders/cancelStatuses`);
  },
  async cancelOrder(params) {
    const userId = store.getters['marketplace/userId'];
    const url = new URL(`${drupalBaseUrl}/users/${userId}/orders/${params.orderNumber}/cancellation`);
    if (params?.email) url.searchParams.append('email', params?.email);
    if (params?.billZip) url.searchParams.append('billZip', params?.billZip);
    if (params?.reason) url.searchParams.append('reason', params?.reason);

    return await axios.post(url.toString());
  },
  async reorderOrder(code) {
    const userId = store.getters['marketplace/userId'];

    return await axios.post(`${drupalBaseUrl}/users/${userId}/orders/reorder?code=${code}`);
  },
  async reorderItem(productCode, quantity) {
    const userId = store.getters['marketplace/userId'];
    const cartId = await this.getCartId();

    return await axios.post(
      `${drupalBaseUrl}/users/${userId}/carts/${cartId}/reorder?productCode=${productCode}&quantity=${quantity}`
    );
  },
  async getReturn(params) {
    const userId = store.getters['marketplace/userId'];
    const url = new URL(`${drupalBaseUrl}/users/${userId}/returns/${params.code}`);
    if (params?.email) url.searchParams.append('email', params?.email);
    if (params?.billZip) url.searchParams.append('billZip', params?.billZip);

    return await axios.get(url.toString());
  },
  async getReturnReasons() {
    const userId = store.getters['marketplace/userId'];

    return await axios.get(`${drupalBaseUrl}/users/${userId}/returnReason`);
  },
  async placeReturn(billZip, email, orderCode, returnRequestEntryInputs) {
    const userId = store.getters['marketplace/userId'];

    return await axios.post(
      `${drupalBaseUrl}/users/${userId}/orderReturns?billZip=${billZip}&email=${email}`,
      {orderCode, returnRequestEntryInputs}
    );
  },
  async getCurrentUserReturns(params) {
    const url = new URL(`${sapBaseUrl}/users/current/returns?`);
    url.searchParams.append('fields', 'DEFAULT');
    url.searchParams.append('currentPage', params?.currentPage);
    url.searchParams.append('pageSize', params?.pageSize);
    if (params?.startDate) url.searchParams.append('startDate', params?.startDate);
    if (params?.endDate) url.searchParams.append('endDate', params?.endDate);
    if (params?.searchTerm) url.searchParams.append('searchTerm', params?.searchTerm);

    return await axios.get(url.toString());
  },
  async getCurrentUserSubscriptions(params) {
    const url = new URL(`${sapBaseUrl}/users/current/subscription?`);
    url.searchParams.append('fields', 'FULL');
    if (params?.status) url.searchParams.append('status', params?.status);

    return await axios.get(url.toString());
  },
  async getCurrentUserSubscription(subscriptionId) {
    const url = new URL(`${sapBaseUrl}/users/current/subscription/${subscriptionId}?fields=FULL`);

    return await axios.get(url.toString());
  },
  async getCancelSubscriptionReasons(subscriptionId) {
    return await axios.get(`${sapBaseUrl}/users/current/subscription/${subscriptionId}/cancelreasons`);
  },
  async cancelSubscription(subscriptionId, cancelReason) {
    return await axios.patch(
      `${sapBaseUrl}/users/current/subscription/${subscriptionId}/cancellation?cancelreason=${cancelReason}`
    );
  },
  async shipNextShipment(subscriptionId) {
    return await axios.patch(
      `${sapBaseUrl}/users/current/subscription/${subscriptionId}/skipnextshipment`
    );
  },
  async updateSubscriptionQuantityFrequency(subscriptionId, quantity, frequency) {
    return await axios.patch(
      `${sapBaseUrl}/users/current/subscription/${subscriptionId}/updatesubscription`,
      {quantity, frequency}
    );
  },
  async getCurrentUserSubscriptionAddresses(subscriptionId) {
    return await axios.get(`${sapBaseUrl}/users/current/subscription/${subscriptionId}/addresses/delivery`);
  },
  async updateCurrentUserSubscriptionAddress(subscriptionId, addressId) {
    return await axios.put(
      `${sapBaseUrl}/users/current/subscription/${subscriptionId}/addresses/delivery/?addressId=${addressId}`
    );
  },
  async getCurrentUserSubscriptionPaymentDetails(subscriptionId) {
    return await axios.get(`${sapBaseUrl}/users/current/subscription/${subscriptionId}/paymentdetails`);
  },
  async updateCurrentUserSubscriptionPayment(subscriptionId, paymentDetailId) {
    return await axios.put(
      `${sapBaseUrl}/users/current/subscription/${subscriptionId}/paymentdetails/?paymentDetailsId=${paymentDetailId}`
    );
  },
  async getCurrentCoupons() {
    return await axios.get(`${sapBaseUrl}/coupon`);
  },
  async applyCoupon(couponCode) {
    const userId = store.getters['marketplace/userId'];
    const cartId = await this.getCartId();

    return await axios.post(
      `${sapBaseUrl}/users/${userId}/carts/${cartId}/applyCoupon`,
      {couponCode}
    );
  },
  async removeCoupon(couponCode) {
    const userId = store.getters['marketplace/userId'];
    const cartId = await this.getCartId();

    return await axios.post(
      `${sapBaseUrl}/users/${userId}/carts/${cartId}/removeCoupon`,
      {couponCode}
    );
  }
};
